// SPDX-License-Identifier: MIT
import type { Ease } from './ease.ts';
/**
 * CSS-style cubic-bezier(x1, y1, x2, y2) easing — the curve vocabulary motion designers use.
 * Solves x(t) = progress with Newton–Raphson, falling back to bisection. y may leave [0, 1]
 * (anticipation / overshoot).
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): Ease {
    const cx = 3 * x1;
    const bx = 3 * (x2 - x1) - cx;
    const ax = 1 - cx - bx;
    const cy = 3 * y1;
    const by = 3 * (y2 - y1) - cy;
    const ay = 1 - cy - by;
    const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
    const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
    const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
    const solve = (x: number) => {
        let t = x;
        for (let i = 0; i < 8; i++) {
            const err = sampleX(t) - x;
            if (Math.abs(err) < 1e-6)
                return t;
            const d = slopeX(t);
            if (Math.abs(d) < 1e-6)
                break;
            t -= err / d;
        }
        let lo = 0;
        let hi = 1;
        t = x;
        for (let i = 0; i < 40; i++) {
            const v = sampleX(t);
            if (Math.abs(v - x) < 1e-6)
                break;
            if (v < x)
                lo = t;
            else
                hi = t;
            t = (lo + hi) / 2;
        }
        return t;
    };
    return (p) => (p <= 0 ? 0 : p >= 1 ? 1 : sampleY(solve(p)));
}
